import React, { useState, useCallback, memo } from 'react';
import MonacoEditor from '@monaco-editor/react';
import styles from './styles/JsonEditor.module.css';

/**
 * A JSON editor component using Monaco Editor
 * 
 * @param {Object} props
 * @param {Object|string} props.value - The JSON value to edit
 * @param {Function} props.onChange - Callback when the value changes
 * @param {number} props.rows - Number of rows to display
 * @param {Function} props.onValidate - Callback for validation errors
 * @param {boolean} props.showErrors - Whether to show validation errors
 */
const JsonEditor = ({
    value,
    onChange,
    rows = 10,
    onValidate,
    showErrors = false,
}) => {
    const [validationErrors, setValidationErrors] = useState([]);

    // Format JSON for display
    const formattedValue = typeof value === 'object'
        ? JSON.stringify(value, null, 2)
        : (value || '');

    // Handle editor changes
    const handleEditorChange = useCallback(newValue => {
        try {
            // If valid JSON, parse it and send the object
            if (newValue.trim()) {
                const parsed = JSON.parse(newValue);
                onChange(parsed);
                setValidationErrors([]);
                if (onValidate) onValidate([]);
            } else {
                onChange('');
                setValidationErrors([]);
                if (onValidate) onValidate([]);
            }
        } catch (e) {
            // If not valid JSON, just pass the string value
            onChange(newValue);
            // Don't set validation errors on every keystroke - 
            // we'll get them from Monaco's validation system
        }
    }, [onChange, onValidate]);

    // Handle validation from Monaco
    const handleEditorValidation = useCallback(markers => {
        // Convert Monaco markers to readable error messages
        const errors = markers.map(marker => ({
            line: marker.startLineNumber,
            message: marker.message,
        }));

        setValidationErrors(errors);
        if (onValidate) onValidate(errors);
    }, [onValidate]);

    return (
        <div className={styles.jsonEditorContainer}>
            <div className={styles.jsonEditor}>
                <MonacoEditor
                    height={`${rows * 19}px`}
                    defaultLanguage="json"
                    value={formattedValue}
                    onChange={handleEditorChange}
                    onValidate={handleEditorValidation}
                    options={{
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        formatOnPaste: true,
                        formatOnType: true,
                        automaticLayout: true,
                        tabSize: 2,
                        scrollbar: {
                            useShadows: false,
                            verticalScrollbarSize: 6,
                            horizontalScrollbarSize: 6,
                            alwaysConsumeMouseWheel: false,
                        },
                    }}
                />
            </div>
            {showErrors && validationErrors.length > 0 && (
                <div className={styles.jsonErrors}>
                    <div className={styles.jsonErrorsTitle}>JSON Validation Errors:</div>
                    <ul className={styles.jsonErrorsList}>
                        {validationErrors.map((error, index) => (
                            <li key={index} className={styles.jsonErrorItem}>
                                Line {error.line}: {error.message}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default memo(JsonEditor);
